import { AmsPermissionClaimType, authService } from '../../../services/Auth';
import {
    ButtonGroup,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
    Spinner,
} from 'reactstrap';
import {
    IdentityUser,
    InstitutionPeopleDto,
    ProfessionDto,
    Program,
    ProgramDirectorUser,
    ProgramDto,
} from '../../../common/Types';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import moment from 'moment';

import { Button } from '../../../components';
import { InstitutionPersonRole } from '../../../common/Data';
import { NavLink } from 'react-router-dom';
import { formatPhoneNumber } from '../../../common/Utils';
import { institutionService } from '../../../services/InstitutionService';
import { personnelService } from '../../../services/Personnel';
import { professionService } from '../../../services/ProfessionAdministration';
import { securityService } from '../../../services/SecurityService';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';

interface ProgramPersonnelProps {
    program: Program;
    type: InstitutionPersonRole;
    personnel: InstitutionPeopleDto[];
    otherPrograms: ProgramDto[];
}

const ProgramPersonnel = (props: ProgramPersonnelProps) => {
    const [rolePeople, setRolePeople] = useState<InstitutionPeopleDto[]>(() =>
        props.personnel.filter((r) => r.roleId === props.type && r.programId === props.program.programId && r.people),
    );
    const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
    const [otherPrograms, setOtherPrograms] = useState<ProgramDto[]>([]);
    const [editing, setEditing] = useState<boolean>(false);
    const [showModal, setModal] = useState<boolean>(false);
    const [identity, setIdentity] = useState<IdentityUser>();
    const [profession, setProfession] = useState<ProfessionDto>();

    // Create empty person template
    const createEmptyPerson = useCallback(
        (): InstitutionPeopleDto =>
            ({
                institutionId: props.program.institutionId,
                institutionPeopleId: 0,
                peopleId: 0,
                programId: props.program.programId,
                roleId: props.type,
                dateCreated: new Date(),
                role: {
                    roleId: props.type,
                    title: InstitutionPersonRole[props.type],
                },
                active: true,
                people: {
                    active: true,
                    editedBy: '00000000-0000-0000-0000-000000000000',
                    createdBy: '00000000-0000-0000-0000-000000000000',
                    dateCreated: '',
                    lastEdited: '',
                    credentials: '',
                    email: '',
                    extension: '',
                    firstName: '',
                    lastName: '',
                    peopleId: 0,
                    phone: '',
                    salutations: '',
                    title: '',
                },
            }) as InstitutionPeopleDto,
        [props.program.institutionId, props.program.programId, props.type],
    );

    // Get current person based on selection
    const currentPerson = useMemo(() => {
        if (rolePeople.length === 0) {
            return createEmptyPerson();
        }

        if (selectedPersonId === null) {
            return rolePeople[0];
        }

        return rolePeople.find((rp) => rp.institutionPeopleId === selectedPersonId) || rolePeople[0];
    }, [rolePeople, selectedPersonId, createEmptyPerson]);

    // Initialize form with current person data
    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors },
        watch,
    } = useForm<InstitutionPeopleDto>({
        defaultValues: currentPerson,
        mode: 'onChange',
    });

    // State for email lookup
    const [isLookingUp, setIsLookingUp] = useState(false);
    const [emailLookupMessage, setEmailLookupMessage] = useState<string | null>(null);

    // Watch email field to control other field states
    const emailValue = watch('people.email');
    const isEmailEntered = !!emailValue && emailValue.trim().length > 0;

    // Email lookup handler - auto-populate fields when email is entered
    const handleEmailBlur = useCallback(
        async (email: string) => {
            setEmailLookupMessage(null);

            // Only lookup if we have a valid email format
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!email || !emailPattern.test(email)) {
                return;
            }

            setIsLookingUp(true);
            try {
                const foundPerson = await personnelService.getPersonByEmail(email);

                if (foundPerson && foundPerson.peopleId) {
                    // Auto-populate all person fields
                    setValue('peopleId', foundPerson.peopleId, { shouldDirty: true });
                    setValue('people.peopleId', foundPerson.peopleId, { shouldDirty: true });
                    setValue('people.firstName', foundPerson.firstName || '', { shouldDirty: true });
                    setValue('people.lastName', foundPerson.lastName || '', { shouldDirty: true });
                    setValue('people.salutations', foundPerson.salutations || '', { shouldDirty: true });
                    setValue('people.credentials', foundPerson.credentials || '', { shouldDirty: true });
                    setValue('people.title', foundPerson.title || '', { shouldDirty: true });
                    setValue('people.phone', foundPerson.phone || '', { shouldDirty: true });
                    setValue('people.extension', foundPerson.extension || '', { shouldDirty: true });
                    setEmailLookupMessage(`Found: ${foundPerson.firstName} ${foundPerson.lastName}`);
                }
            } catch (error) {
                console.log('Email lookup failed:', error);
            } finally {
                setIsLookingUp(false);
            }
        },
        [setValue],
    );

    // Update form when person changes
    useEffect(() => {
        reset(currentPerson);
        setEditing(false);
    }, [currentPerson, reset]);

    // Update selected person when rolePeople changes
    useEffect(() => {
        if (rolePeople.length > 0 && selectedPersonId === null) {
            setSelectedPersonId(rolePeople[0].institutionPeopleId);
        }
    }, [rolePeople, selectedPersonId]);

    const toggleModal = useCallback(() => setModal((prev) => !prev), []);

    const reloadPersonnelFromServer = useCallback(async () => {
        if (!props.program) return;

        try {
            const results = await personnelService.getAmsPersonnelByInsitutionId(props.program.institutionId);
            const filtered = results.filter(
                (r) =>
                    (!r.programId || r.programId === props.program.programId) &&
                    r.active === true &&
                    r.people?.active === true &&
                    r.roleId === props.type,
            );
            setRolePeople(filtered);
            setModal(false);

            // Select first person after reload
            if (filtered.length > 0) {
                setSelectedPersonId(filtered[0].institutionPeopleId);
            }
        } catch (error) {
            toast.error('Error loading personnel');
        }
    }, [props.program, props.type]);

    useEffect(() => {
        if (currentPerson && props.otherPrograms) {
            const samePersonSameRoleOtherPrograms = props.personnel.filter(
                (r) =>
                    r.roleId === props.type &&
                    r.peopleId === currentPerson.peopleId &&
                    r.programId !== props.program.programId,
            );

            setOtherPrograms([
                ...props.otherPrograms.filter((op) =>
                    samePersonSameRoleOtherPrograms.find((spop) => spop.programId === op.programId),
                ),
            ]);

            if (
                props.type === InstitutionPersonRole['Program Director'] &&
                currentPerson.people?.email &&
                authService.hasPermission(AmsPermissionClaimType, 'systemadmin') &&
                !identity
            ) {
                securityService
                    .loadProgramDirector(currentPerson.people.email, props.program.programId)
                    .then((result) => {
                        setIdentity(result);
                    })
                    .catch(() => {
                        toast.warning('Unable to validate Program Director login.');
                    });
            }
        }
    }, [
        currentPerson?.peopleId,
        currentPerson?.people?.email,
        props.type,
        props.program.programId,
        props.otherPrograms,
        props.personnel,
        identity,
    ]);

    useEffect(() => {
        const loadProfession = () => {
            if (props.program.professionId) {
                professionService
                    .getProfesssion(props.program.professionId)
                    .then((prof) => {
                        setProfession(prof);
                    })
                    .catch(() => {
                        toast.error('Error loading profession detail.');
                    });
            }
        };

        loadProfession();
    }, [props.program.professionId, setProfession]);

    const cancelForm = useCallback(() => {
        setEditing(false);
        reset(currentPerson);
    }, [currentPerson, reset]);

    const createProgramDirectorLogin = useCallback(async () => {
        if (!currentPerson?.people) return;

        const toastId = toast.info('Creating Program Director login...');

        try {
            await securityService.updateProgramDirector(
                new ProgramDirectorUser({
                    ...currentPerson.people,
                    programId: props.program.programId,
                }),
            );

            await reloadPersonnelFromServer();
            toast.update(toastId, {
                type: 'success',
                render: 'Login created successfully',
            });
        } catch (error) {
            toast.update(toastId, {
                type: 'error',
                render: 'Error creating login',
            });
        }
    }, [currentPerson, props.program.programId, reloadPersonnelFromServer]);

    const removeProgramDirectorLogin = () => {
        if (currentPerson?.people) {
            const toastId = toast.info('Removing Program Director login...');

            securityService
                .removeProgramDirector(
                    new ProgramDirectorUser({
                        ...currentPerson.people,
                        programId: props.program.programId,
                    }),
                )
                .then(() => {
                    reloadPersonnelFromServer();
                    toast.update(toastId, {
                        type: 'success',
                        render: 'Login removed successfully',
                    });
                })
                .catch(() => {
                    toast.error('Error removing login');
                });
        }
    };

    const sendPasswordResetEmail = () => {
        if (identity?.id) {
            const toastId = toast.info('Sending password reset email...');

            securityService
                .initiatePasswordReset(identity.id)
                .then(() => {
                    reloadPersonnelFromServer();
                    toast.update(toastId, {
                        type: 'success',
                        render: 'Password reset email sent successfully',
                    });
                })
                .catch(() => {
                    toast.error('Error sending password reset email');
                });
        }
    };

    const submitForm = useCallback(
        async (values: InstitutionPeopleDto) => {
            const toastId = toast.info('Saving person...');

            try {
                await institutionService.saveInstitutionPerson(new InstitutionPeopleDto(values));

                if (
                    props.type === InstitutionPersonRole['Program Director'] &&
                    profession?.armsUser === true &&
                    rolePeople.length <= 1
                ) {
                    try {
                        await securityService.updateProgramDirector(
                            new ProgramDirectorUser({
                                ...values.people,
                                programId: props.program.programId,
                            }),
                        );
                    } catch (directorError) {
                        toast.error('Error updating program director permissions');
                        return;
                    }
                }

                await reloadPersonnelFromServer();
                setEditing(false);
                toast.update(toastId, {
                    type: 'success',
                    render: 'Person saved successfully',
                    autoClose: 3000,
                });
            } catch (error) {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Error saving person',
                    autoClose: 5000,
                });
            }
        },
        [
            props.type,
            props.program.programId,
            profession?.armsUser,
            rolePeople.length,
            reloadPersonnelFromServer,
        ],
    );

    const removePerson = useCallback(async () => {
        if (!currentPerson) return;

        const toastId = toast.info('Removing person...');

        try {
            await institutionService.removeInstitutionPerson(currentPerson);

            if (props.type === InstitutionPersonRole['Program Director']) {
                try {
                    await securityService.removeProgramDirector(
                        new ProgramDirectorUser({
                            ...currentPerson.people,
                            programId: props.program.programId,
                        }),
                    );
                } catch {
                    toast.error('Error updating program director permissions');
                }
            }

            await reloadPersonnelFromServer();
            setSelectedPersonId(null);
            toast.update(toastId, {
                type: 'success',
                render: 'Person removed successfully',
                autoClose: 3000,
            });
        } catch (error) {
            toast.update(toastId, {
                type: 'error',
                render: 'Error removing person',
                autoClose: 5000,
            });
        }
    }, [currentPerson, props.type, props.program.programId, reloadPersonnelFromServer]);

    const handlePersonSelect = useCallback(
        (personId: number) => {
            if (!editing) {
                setSelectedPersonId(personId);
            }
        },
        [editing],
    );

    const canSendPasswordReset = (): boolean => {
        return (
            identity !== undefined && identity.email !== undefined && identity.email !== null && identity.email !== ''
        );
    };

    const isMissingIdentity = (): boolean => {
        return (
            props.type === InstitutionPersonRole['Program Director'] &&
            identity !== undefined &&
            (identity.email === null || identity.email === undefined || identity.email === '')
        );
    };

    const isCannotCreateLogin = (): boolean => {
        return (
            props.type === InstitutionPersonRole['Program Director'] &&
            !isMissingIdentity() &&
            currentPerson !== undefined &&
            currentPerson.institutionPeopleId > 0 &&
            (currentPerson.people?.email === undefined || currentPerson.people?.email === null)
        );
    };

    const editedByInfo = useMemo(() => {
        if (currentPerson?.people?.editedByString && currentPerson.people.editedByString.indexOf('{') > -1) {
            try {
                return JSON.parse(currentPerson.people.editedByString);
            } catch {
                return undefined;
            }
        }
        return undefined;
    }, [currentPerson]);

    const hasAdminPermission = useMemo(() => authService.hasPermission(AmsPermissionClaimType, 'systemadmin'), []);

    return (
        <>
            <Form noValidate className="flex-grow-1 d-flex" onSubmit={handleSubmit(submitForm)}>
                <Card className="align-self-stretch flex-grow-1">
                    {rolePeople.length > 1 && (
                        <CardHeader className="bg-warning d-flex justify-content-between">
                            <div>
                                <h4 className="mb-0">{InstitutionPersonRole[props.type]}</h4>
                            </div>
                            <div className="d-flex flex-end align-items-center">
                                {rolePeople.map((rp) => (
                                    <Button
                                        key={rp.institutionPeopleId}
                                        type="button"
                                        className="btn-sm ml-1"
                                        onClick={() => handlePersonSelect(rp.institutionPeopleId)}
                                        disabled={editing}
                                        color={
                                            currentPerson?.institutionPeopleId === rp.institutionPeopleId
                                                ? 'primary'
                                                : 'secondary'
                                        }
                                    >
                                        <span style={{ fontSize: '11px' }}>
                                            {`${rp.people?.firstName} ${rp.people?.lastName}`}
                                        </span>
                                    </Button>
                                ))}
                            </div>
                        </CardHeader>
                    )}
                    <CardBody>
                        {rolePeople.length <= 1 && (
                            <h4 className="card-title d-flex align-items-center justify-content-between">
                                <span>{InstitutionPersonRole[props.type]}</span>
                                <div>
                                    {canSendPasswordReset() && (
                                        <Button
                                            type="button"
                                            color="warning"
                                            className="btn-sm"
                                            onClick={() => sendPasswordResetEmail()}
                                        >
                                            Send Password Reset Email
                                        </Button>
                                    )}
                                    {canSendPasswordReset() && (
                                        <Button
                                            type="button"
                                            color="danger"
                                            className="btn-sm ms-2"
                                            onClick={() => removeProgramDirectorLogin()}
                                        >
                                            Remove Login
                                        </Button>
                                    )}
                                    {isMissingIdentity() && (
                                        <Button
                                            type="button"
                                            color="primary"
                                            className="btn-sm"
                                            onClick={() => createProgramDirectorLogin()}
                                        >
                                            Create Login
                                        </Button>
                                    )}
                                    {isCannotCreateLogin() && (
                                        <h6 className="text-danger">Email is required for a login</h6>
                                    )}
                                </div>
                            </h4>
                        )}

                        {/* Hidden fields */}
                        <Controller
                            name="institutionPeopleId"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            name="institutionId"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            name="programId"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            name="peopleId"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            name="roleId"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                        />

                        <Row>
                            <Col>
                                <FormGroup className="row">
                                    <Col className="d-flex flex-column">
                                        <Label>
                                            Email{' '}
                                            {isLookingUp && (
                                                <Spinner size="sm" color="primary" className="ms-1" />
                                            )}
                                        </Label>
                                        {editing ? (
                                            <Controller
                                                name="people.email"
                                                control={control}
                                                rules={{
                                                    required:
                                                        props.type === InstitutionPersonRole['Program Director']
                                                            ? 'Email is required for Program Director'
                                                            : false,
                                                    pattern: {
                                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                                        message: 'Must be a valid email',
                                                    },
                                                }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input
                                                            type="email"
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) => field.onChange(e.target.value || '')}
                                                            onBlur={(e) => {
                                                                field.onBlur();
                                                                handleEmailBlur(e.target.value);
                                                            }}
                                                            invalid={!!errors.people?.email}
                                                        />
                                                        {emailLookupMessage && (
                                                            <span className="text-success small">
                                                                {emailLookupMessage}
                                                            </span>
                                                        )}
                                                        {errors.people?.email && (
                                                            <span className="text-danger">
                                                                {errors.people.email.message}
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            />
                                        ) : (
                                            <span>{currentPerson?.people?.email || ''}</span>
                                        )}
                                    </Col>
                                    <Col className="d-flex flex-column">
                                        <Label>Job Title</Label>
                                        {editing ? (
                                            <Controller
                                                name="people.title"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        type="text"
                                                        disabled={!isEmailEntered}
                                                        {...field}
                                                        value={field.value || ''}
                                                        onChange={(e) => field.onChange(e.target.value || '')}
                                                    />
                                                )}
                                            />
                                        ) : (
                                            <span>{currentPerson?.people?.title || ''}</span>
                                        )}
                                    </Col>
                                </FormGroup>

                                <FormGroup className="row">
                                    <Col className="d-flex flex-column">
                                        <Label>Salutations</Label>
                                        {editing ? (
                                            <Controller
                                                name="people.salutations"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        type="text"
                                                        disabled={!isEmailEntered}
                                                        {...field}
                                                        value={field.value || ''}
                                                        onChange={(e) => field.onChange(e.target.value || '')}
                                                    />
                                                )}
                                            />
                                        ) : (
                                            <span>{currentPerson?.people?.salutations || ''}</span>
                                        )}
                                    </Col>
                                    <Col className="d-flex flex-column">
                                        <Label>Credentials</Label>
                                        {editing ? (
                                            <Controller
                                                name="people.credentials"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        type="text"
                                                        disabled={!isEmailEntered}
                                                        {...field}
                                                        value={field.value || ''}
                                                        onChange={(e) => field.onChange(e.target.value || '')}
                                                    />
                                                )}
                                            />
                                        ) : (
                                            <span>{currentPerson?.people?.credentials || ''}</span>
                                        )}
                                    </Col>
                                </FormGroup>

                                <FormGroup className="row">
                                    <Col className="d-flex flex-column">
                                        <Label>First Name</Label>
                                        {editing ? (
                                            <Controller
                                                name="people.firstName"
                                                control={control}
                                                rules={{ required: 'First name is required' }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input
                                                            type="text"
                                                            disabled={!isEmailEntered}
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) => field.onChange(e.target.value || '')}
                                                            invalid={!!errors.people?.firstName}
                                                        />
                                                        {errors.people?.firstName && (
                                                            <span className="text-danger">
                                                                {errors.people.firstName.message}
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            />
                                        ) : (
                                            <span>{currentPerson?.people?.firstName || ''}</span>
                                        )}
                                    </Col>
                                    <Col className="d-flex flex-column">
                                        <Label>Last Name</Label>
                                        {editing ? (
                                            <Controller
                                                name="people.lastName"
                                                control={control}
                                                rules={{ required: 'Last name is required' }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input
                                                            type="text"
                                                            disabled={!isEmailEntered}
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) => field.onChange(e.target.value || '')}
                                                            invalid={!!errors.people?.lastName}
                                                        />
                                                        {errors.people?.lastName && (
                                                            <span className="text-danger">
                                                                {errors.people.lastName.message}
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            />
                                        ) : (
                                            <span>{currentPerson?.people?.lastName || ''}</span>
                                        )}
                                    </Col>
                                </FormGroup>

                                {props.type !== InstitutionPersonRole.Dean && (
                                    <FormGroup className="row">
                                        <Col className="d-flex flex-column">
                                            <Label>Phone</Label>
                                            {editing ? (
                                                <Controller
                                                    name="people.phone"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            disabled={!isEmailEntered}
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) => field.onChange(e.target.value || '')}
                                                        />
                                                    )}
                                                />
                                            ) : (
                                                <div>
                                                    {formatPhoneNumber(currentPerson?.people?.phone)}{' '}
                                                    {currentPerson?.people?.extension
                                                        ? `ext ${currentPerson.people.extension}`
                                                        : ''}
                                                </div>
                                            )}
                                        </Col>
                                        {editing && (
                                            <Col className="d-flex flex-column">
                                                <Label>Extension</Label>
                                                <Controller
                                                    name="people.extension"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            disabled={!isEmailEntered}
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) => field.onChange(e.target.value || '')}
                                                        />
                                                    )}
                                                />
                                            </Col>
                                        )}
                                    </FormGroup>
                                )}
                            </Col>
                        </Row>

                        {otherPrograms && otherPrograms.length > 0 && (
                            <Row>
                                <Col className="d-flex flex-column align-items-start">
                                    <Label>Assigned to Other Programs</Label>
                                    <div className="d-flex flex-wrap">
                                        {otherPrograms.map((op) => (
                                            <NavLink
                                                key={op.programId}
                                                to={`/institution/${op.institutionId}/program/${op.programId}`}
                                                className="btn btn-sm btn-secondary mb-2 me-2"
                                            >
                                                {op.title}
                                            </NavLink>
                                        ))}
                                    </div>
                                </Col>
                            </Row>
                        )}

                        {currentPerson &&
                            (currentPerson.peopleId || 0) > 0 &&
                            (currentPerson.people?.lastEdited || editedByInfo) && (
                                <Row>
                                    <Col className="font-italic">
                                        <span>Last edited</span>
                                        {editedByInfo && <span> by {editedByInfo.name}</span>}
                                        {currentPerson.people?.lastEdited && (
                                            <span>
                                                {` on ${moment
                                                    .utc(currentPerson.people.lastEdited)
                                                    .local()
                                                    .format('MM/DD/YYYY [at] hh:mm A')}`}
                                            </span>
                                        )}
                                    </Col>
                                </Row>
                            )}
                    </CardBody>

                    {hasAdminPermission && (
                        <CardFooter className="bg-white border-0 p-0">
                            <ButtonGroup className="w-100">
                                {!editing && (
                                    <>
                                        {currentPerson && currentPerson.institutionPeopleId > 0 && (
                                            <>
                                                <Button color="info" type="button" onClick={() => setEditing(true)}>
                                                    <i className="mdi mdi-pencil" />
                                                    <span className="ml-1">Edit Person</span>
                                                </Button>
                                                <Button color="danger" type="button" onClick={toggleModal}>
                                                    <i className="mdi mdi-delete" />
                                                    <span className="ml-1">Remove Person</span>
                                                </Button>
                                            </>
                                        )}
                                        {currentPerson && currentPerson.institutionPeopleId === 0 && (
                                            <Button color="info" type="button" onClick={() => setEditing(true)}>
                                                <i className="mdi mdi-account-plus" />
                                                <span className="ml-1">Add Person</span>
                                            </Button>
                                        )}
                                    </>
                                )}
                                {editing && (
                                    <>
                                        <Button color="success" type="submit">
                                            <i className="mdi mdi-content-save" />
                                            <span className="ml-1">Submit</span>
                                        </Button>
                                        <Button color="white" type="button" onClick={cancelForm}>
                                            <span className="ml-1">Cancel</span>
                                        </Button>
                                    </>
                                )}
                            </ButtonGroup>
                        </CardFooter>
                    )}
                </Card>
            </Form>

            {currentPerson && currentPerson.people && currentPerson.people.peopleId > 0 && (
                <Modal isOpen={showModal} toggle={toggleModal} centered>
                    <ModalHeader>
                        {`Confirm Remove ${currentPerson.people.firstName} ${currentPerson.people.lastName} (${
                            InstitutionPersonRole[props.type]
                        })`}
                    </ModalHeader>
                    <ModalBody>
                        <p>
                            {`You have selected to remove ${currentPerson.people.firstName} ${
                                currentPerson.people.lastName
                            } from the role of ${InstitutionPersonRole[props.type]} at ${
                                props.program.title
                            }. To continue, please click the Confirm button below.`}
                        </p>
                    </ModalBody>
                    <ModalFooter>
                        <div className="d-flex justify-content-between flex-grow-1">
                            <Button color="primary" onClick={removePerson} type="button">
                                Confirm
                            </Button>
                            <Button color="secondary" onClick={toggleModal} type="button">
                                Cancel
                            </Button>
                        </div>
                    </ModalFooter>
                </Modal>
            )}
        </>
    );
};

export default ProgramPersonnel;